import { FilterPill, IconButton } from "@somethings-ui/web";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";
import { useNoticeAnalytics } from "./notice-analytics";
import {
  getNoticeFilterCounts,
  type NoticeConversation,
  type NoticeFilterId,
} from "./notice-conversations";

interface NoticeFiltersProps {
  conversations: NoticeConversation[];
  activeFilter: NoticeFilterId;
  onFilterChange: (filter: NoticeFilterId) => void;
}

export function NoticeFilters({
  conversations,
  activeFilter,
  onFilterChange,
}: NoticeFiltersProps) {
  const counts = getNoticeFilterCounts(conversations);
  const { trackFilterApply } = useNoticeAnalytics();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const filterCounts: Record<NoticeFilterId, number> = {
    all: conversations.length,
    ...counts,
  };

  const filters: { id: NoticeFilterId; label: string }[] = [
    { id: "all", label: "All" },
    { id: "needsResponse", label: `${counts.needsResponse} needs response` },
    { id: "unread", label: `${counts.unread} unread` },
    { id: "checkIn", label: `${counts.checkIn} check-in` },
    {
      id: "newTeens",
      label: `${counts.newTeens} new teen${counts.newTeens === 1 ? "" : "s"}`,
    },
  ];

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 1);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    return () => el.removeEventListener("scroll", update);
  });

  const handleFilterClick = (filter: NoticeFilterId) => {
    trackFilterApply(filter, filterCounts[filter]);
    onFilterChange(filter);
  };

  const scrollPills = (direction: -1 | 1) => {
    scrollerRef.current?.scrollBy({
      left: direction * 160,
      behavior: "smooth",
    });
  };

  return (
    <div className="relative shrink-0 border-b border-surfaces-divider">
      <div
        ref={scrollerRef}
        className="flex items-center gap-2 overflow-x-auto px-4 pb-[13px] pt-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {filters.map((filter) => (
          <FilterPill
            key={filter.id}
            label={filter.label}
            active={activeFilter === filter.id}
            onClick={() => handleFilterClick(filter.id)}
          />
        ))}
      </div>

      {canScrollLeft ? (
        <>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-[63px] bg-gradient-to-r from-surfaces-z3 to-transparent" />
          <IconButton
            variant="tertiary"
            size="md"
            shape="circle"
            aria-label="Show earlier filters"
            icon={<ChevronLeft className="size-4" />}
            onClick={() => scrollPills(-1)}
            className="absolute left-[15px] top-[7px] border border-surfaces-z5 bg-surfaces-z3"
          />
        </>
      ) : null}

      {canScrollRight ? (
        <>
          <div className="pointer-events-none absolute inset-y-0 right-0 w-[63px] bg-gradient-to-l from-surfaces-z3 to-transparent" />
          <IconButton
            variant="tertiary"
            size="md"
            shape="circle"
            aria-label="Show more filters"
            icon={<ChevronRight className="size-4" />}
            onClick={() => scrollPills(1)}
            className="absolute right-[5px] top-[7px] border border-surfaces-z5 bg-surfaces-z3"
          />
        </>
      ) : null}
    </div>
  );
}
